<template>
  <div>
    <dx-grid
      url="marathonPickupContacts"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      :selectionEnabled="true"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <v-dialog persistent v-model="editDialog" max-width="860">
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">{{
          row.id ? "Update class" : "Add class"
        }}</v-card-title>
        <v-layout row wrap>
          <v-flex xs12 sm4 class="px-2">
            <v-autocomplete
              :items="branchItems"
              v-model="row.branch"
              label="Branch"
              ref="branch"
              :rules="[branchRule]"
              @change="onBranchPicked"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-autocomplete
              :items="classItems"
              v-model="row.class"
              label="Class"
              ref="className"
              :rules="[classRule]"
              @change="onClassPicked"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-autocomplete
              :items="sectionItems"
              v-model="row.section"
              label="Section"
              ref="section"
              :rules="[sectionRule]"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6 class="px-2">
            <v-text-field
              v-model="row.teacherName"
              label="Teacher name"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="px-2">
            <v-text-field
              v-model="row.hebrewName"
              label="Hebrew name"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field
              v-model="row.teacherCode"
              label="Teacher code"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field
              v-model="row.employeeID"
              label="Employee ID"
              type="number"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field v-model="row.room" label="Room"></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field
              ref="did"
              v-model="row.did"
              label="DID"
              mask="###-###-####"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field
              ref="alertPhone"
              v-model="row.alertPhone"
              label="Alert phone"
              mask="###-###-####"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="px-2">
            <v-text-field
              ref="cellPhone"
              v-model="row.cellPhone"
              label="Cell phone"
              mask="###-###-####"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="px-2">
            <v-text-field v-model="row.subName" label="Sub"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="px-2">
            <v-text-field
              ref="subCell"
              v-model="row.subCell"
              label="Sub cell"
              mask="###-###-####"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 class="px-2 mt-2">
            <v-divider class="mb-3"></v-divider>
            <v-layout row align-center class="mb-1">
              <div class="subheading font-weight-medium">After deadline</div>
              <span class="grey--text ml-2">
                After this time (New York), calls go to this number instead of
                the teacher.
              </span>
              <v-spacer></v-spacer>
              <v-btn
                v-if="hasRowDeadline"
                color="error"
                dark
                small
                class="ma-0"
                @click="clearRowDeadline"
                >Clear deadline</v-btn
              >
            </v-layout>
            <div v-if="deadlineTimeItems.length" class="mb-2">
              <span class="grey--text mr-1">Times already used:</span>
              <v-chip
                v-for="time in deadlineTimeItems"
                :key="time"
                small
                :color="
                  matchingDeadline(row.deadlineTime) === time ? 'primary' : ''
                "
                :text-color="
                  matchingDeadline(row.deadlineTime) === time ? 'white' : ''
                "
                @click="applyExistingDeadline('row', time)"
                >{{ time }}</v-chip
              >
            </div>
            <v-layout row wrap align-end>
              <v-flex xs12 sm6 class="pr-2">
                <div class="font-weight-regular grey--text mb-1">
                  Deadline time
                </div>
                <DxDateBox
                  type="time"
                  picker-type="list"
                  :interval="15"
                  :show-clear-button="true"
                  display-format="hh:mm a"
                  placeholder="Pick a time"
                  v-model="row.deadlineTime"
                />
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-combobox
                  ref="afterDeadlinePhone"
                  :items="deadlinePhoneItems"
                  v-model="row.afterDeadlinePhone"
                  label="Transfer to phone"
                  mask="###-###-####"
                  hint="Pick a number already used, or type a new one."
                  persistent-hint
                  clearable
                  :return-object="false"
                  :rules="[rules.phone]"
                ></v-combobox>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
        <div class="px-2 pb-2">
          Calls connect to <strong>{{ connectPreview }}</strong
          ><span v-if="afterDeadlinePreview">
            . After {{ afterDeadlinePreview }} they connect to
            <strong>{{ formatPhone(row.afterDeadlinePhone) }}</strong></span
          >.
        </div>
        <v-switch
          class="mx-2 mt-0"
          color="success"
          v-model="row.isActive"
          label="Active"
          hide-details
        ></v-switch>
        <v-card-actions class="mx-0 px-2 pb-2 mt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="editDialog = false">Cancel</v-btn>
          <v-btn
            v-if="row.id"
            color="error"
            dark
            @click="confirmDelete = true"
            >Delete</v-btn
          >
          <v-btn :loading="processing" color="success" @click="save"
            >Save</v-btn
          >
        </v-card-actions>
        <v-dialog v-model="confirmDelete" max-width="420">
          <v-card class="pa-3">
            <v-card-title class="headline pl-0 pt-0"
              >Delete this class?</v-card-title
            >
            <v-card-text class="px-0">
              {{ row.teacherName }} {{ row.branch }} {{ row.class }}
              {{ row.section }} will be removed from the pickup line.
            </v-card-text>
            <v-card-actions class="px-0">
              <v-spacer></v-spacer>
              <v-btn color="default" dark @click="confirmDelete = false"
                >Cancel</v-btn
              >
              <v-btn color="error" dark :loading="processing" @click="remove"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="lineDialog" max-width="420">
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">Pickup line</v-card-title>
        <v-switch
          class="mt-0"
          color="success"
          v-model="lineDraft"
          label="Line is active"
          hide-details
        ></v-switch>
        <div class="grey--text mt-3">
          While the line is off, callers hear that it is not active.
        </div>
        <v-card-actions class="px-0 pb-0 mt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="lineDialog = false">Cancel</v-btn>
          <v-btn :loading="savingLine" color="success" @click="saveLineSetting"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="deadlineDialog" max-width="480">
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0"
          >After deadline</v-card-title
        >
        <div class="grey--text mb-3">
          {{ deadlineSelection.length }} selected. After this time (New York),
          calls go to this number instead of the teacher.
        </div>
        <div v-if="deadlineTimeItems.length" class="mb-2">
          <span class="grey--text mr-1">Times already used:</span>
          <v-chip
            v-for="time in deadlineTimeItems"
            :key="time"
            small
            :color="matchingDeadline(deadlineTime) === time ? 'primary' : ''"
            :text-color="matchingDeadline(deadlineTime) === time ? 'white' : ''"
            @click="applyExistingDeadline('bulk', time)"
            >{{ time }}</v-chip
          >
        </div>
        <div class="font-weight-regular grey--text mb-1">Deadline time</div>
        <DxDateBox
          class="mb-3"
          type="time"
          picker-type="list"
          :interval="15"
          :show-clear-button="true"
          display-format="hh:mm a"
          placeholder="Pick a time"
          v-model="deadlineTime"
        />
        <v-combobox
          ref="bulkDeadlinePhone"
          :items="deadlinePhoneItems"
          v-model="deadlinePhone"
          label="Transfer to phone"
          mask="###-###-####"
          hint="Pick a number already used, or type a new one."
          persistent-hint
          clearable
          :return-object="false"
          :rules="[rules.phone]"
        ></v-combobox>
        <v-card-actions class="px-0 pb-0 mt-3">
          <v-btn
            color="error"
            dark
            :loading="savingDeadline"
            @click="removeBulkDeadline"
            >Remove deadline</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deadlineDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="savingDeadline"
            color="success"
            @click="saveDeadline"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import DxDateBox from "devextreme-vue/date-box";

function padTime(value) {
  return (value < 10 ? "0" : "") + value;
}

function timeValue(value) {
  if (!value) return "";
  if (value instanceof Date && !isNaN(value.getTime())) {
    return padTime(value.getHours()) + ":" + padTime(value.getMinutes());
  }
  const text = String(value).trim();
  if (text.indexOf("T") !== -1) {
    const date = new Date(text);
    if (!isNaN(date.getTime())) {
      return padTime(date.getHours()) + ":" + padTime(date.getMinutes());
    }
  }
  const ampm = text.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2]);
    if (hour < 1 || hour > 12 || minute > 59) return "";
    const isPm = ampm[3].toLowerCase() === "pm";
    if (isPm && hour < 12) hour += 12;
    if (!isPm && hour === 12) hour = 0;
    return padTime(hour) + ":" + padTime(minute);
  }

  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return "";
  return padTime(hour) + ":" + padTime(minute);
}

function toDeadlineDate(value) {
  const time = timeValue(value);
  if (!time) return null;
  const parts = time.split(":");
  const date = new Date();
  date.setHours(Number(parts[0]), Number(parts[1]), 0, 0);
  return date;
}

function formatDeadline(value) {
  const time = timeValue(value);
  if (!time) return "";
  const parts = time.split(":");
  let hour = Number(parts[0]);
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return padTime(hour) + ":" + parts[1] + " " + suffix;
}

function column(label, field, extra) {
  return Object.assign(
    {
      label: label,
      field: field,
      visible: true,
      filterValues: [],
      filterValue: null,
      selectedFilterOperation: null,
    },
    extra || {}
  );
}

function emptyRow() {
  return {
    id: 0,
    branch: "",
    class: "",
    section: "",
    employeeID: null,
    teacherCode: "",
    hebrewName: "",
    teacherName: "",
    alertPhone: "",
    cellPhone: "",
    room: "",
    subName: "",
    subCell: "",
    did: "",
    deadlineTime: null,
    afterDeadlinePhone: "",
    isActive: true,
  };
}

export default {
  components: {
    DxDateBox,
  },
  data() {
    return {
      title: "Marathon Pickup",
      actions: [],
      lineActive: false,
      lineDraft: false,
      lineDialog: false,
      savingLine: false,
      editDialog: false,
      confirmDelete: false,
      processing: false,
      assigningRow: false,
      row: emptyRow(),
      classOptions: [],
      deadlineDialog: false,
      deadlineSelection: [],
      deadlineTime: null,
      deadlinePhone: "",
      deadlinePhoneItems: [],
      deadlineTimeItems: [],
      savingDeadline: false,
      rules: {
        required: function(value) {
          return !!value || "Required";
        },
        phone: function(value) {
          const text = (value || "").toString().trim();
          if (!text || !text.replace(/[-_\s]/g, "")) return true;
          const digits = text.replace(/\D/g, "");
          if (digits.length === 10) return true;
          if (digits.length === 11 && digits.charAt(0) === "1") return true;
          if (digits.length === 7) return true;
          return "Use a 10 digit phone number.";
        },
      },
      columns: [
        column("Branch", "branch", { sortOrder: "asc", sortIndex: 0 }),
        column("Class", "class", { sortOrder: "asc", sortIndex: 1 }),
        column("Section", "section", { sortOrder: "asc", sortIndex: 2 }),
        column("Teacher", "teacherName"),
        column("Hebrew name", "hebrewName"),
        column("DID", "did"),
        column("Alert phone", "alertPhone"),
        column("Cell phone", "cellPhone"),
        column("Connects to", "connectPhone"),
        column("Deadline", "deadlineTime"),
        column("After deadline", "afterDeadlinePhone"),
        column("Room", "room"),
        column("Sub", "subName"),
        column("Sub cell", "subCell"),
        column("Teacher code", "teacherCode", { visible: false }),
        column("Employee ID", "employeeID", { visible: false }),
        column("Active", "isActive", { type: "boolean" }),
      ],
    };
  },
  computed: {
    connectPreview() {
      return (
        this.digits(this.row.did) ||
        this.digits(this.row.cellPhone) ||
        this.digits(this.row.alertPhone) ||
        "no number yet"
      );
    },
    afterDeadlinePreview() {
      const time = formatDeadline(this.row.deadlineTime);
      const phone = (this.row.afterDeadlinePhone || "").toString().trim();
      if (!time || !phone) return "";
      return time;
    },
    hasRowDeadline() {
      return (
        !!timeValue(this.row.deadlineTime) ||
        !!this.digits(this.row.afterDeadlinePhone)
      );
    },
    branchItems() {
      return this.unique(this.classOptions.map((option) => option.branch));
    },
    classItems() {
      const branch = (this.row.branch || "").trim();
      if (!branch) return [];
      return this.unique(
        this.classOptions
          .filter((option) => option.branch === branch)
          .map((option) => option.class)
      );
    },
    sectionItems() {
      const branch = (this.row.branch || "").trim();
      const className = (this.row.class || "").trim();
      if (!branch || !className) return [];
      return this.unique(
        this.classOptions
          .filter(
            (option) =>
              option.branch === branch && option.class === className
          )
          .map((option) => option.section)
      );
    },
  },
  created() {
    this.loadClassOptions();
    this.loadDeadlineChoices();
  },
  methods: {
    async beforeSetupGrid() {
      try {
        const setting = await this.$http.get(
          "api/marathonPickupContacts/lineEnabled"
        );
        this.lineActive = !!(setting && setting.enabled);
      } catch (e) {
        this.lineActive = false;
      }

      this.actions = [
        {
          text: "Add Class",
          action: this.addContact,
        },
        {
          text: "After Deadline",
          showOnSelection: true,
          action: this.openDeadlineDialog,
        },
        {
          text: "Line Settings",
          action: this.openLineDialog,
        },
      ];
    },
    openLineDialog() {
      this.lineDraft = this.lineActive;
      this.lineDialog = true;
    },
    async saveLineSetting() {
      const enabled = !!this.lineDraft;
      this.savingLine = true;
      try {
        await this.$http.post("api/marathonPickupContacts/lineEnabled", {
          enabled: enabled,
        });
        this.lineActive = enabled;
        this.lineDialog = false;
        this.$notify({
          group: "notification",
          text: enabled
            ? "Pickup line is on."
            : "Pickup line is off. Callers will hear that it is not active.",
          type: "success",
        });
      } finally {
        this.savingLine = false;
      }
    },
    async loadDeadlineChoices() {
      try {
        const phones = await this.$http.get(
          "api/marathonPickupContacts/deadlinePhones"
        );
        this.deadlinePhoneItems = this.unique(phones || []).map((phone) =>
          this.formatPhone(phone)
        );
      } catch (e) {
        this.deadlinePhoneItems = [];
      }

      try {
        const times = await this.$http.get(
          "api/marathonPickupContacts/deadlineTimes",
          { notifyError: false }
        );
        this.deadlineTimeItems = this.unique(times || [])
          .map((time) => timeValue(time))
          .filter((time) => time)
          .sort()
          .map((time) => formatDeadline(time));
      } catch (e) {
        this.deadlineTimeItems = [];
      }
    },
    matchingDeadline(value) {
      const label = formatDeadline(value);
      if (!label || this.deadlineTimeItems.indexOf(label) === -1) return null;
      return label;
    },
    applyExistingDeadline(target, label) {
      const date = label ? toDeadlineDate(label) : null;
      if (target === "row") this.row.deadlineTime = date;
      else this.deadlineTime = date;
    },
    clearRowDeadline() {
      this.row.deadlineTime = null;
      this.row.afterDeadlinePhone = "";
      if (this.$refs.afterDeadlinePhone) {
        this.$refs.afterDeadlinePhone.resetValidation();
      }
    },
    async removeBulkDeadline() {
      this.deadlineTime = null;
      this.deadlinePhone = "";
      if (this.$refs.bulkDeadlinePhone) {
        this.$refs.bulkDeadlinePhone.resetValidation();
      }
      await this.saveDeadline();
    },
    formatPhone(value) {
      const digits = (value || "").toString().replace(/\D/g, "");
      const ten =
        digits.length === 11 && digits.charAt(0) === "1"
          ? digits.substring(1)
          : digits;
      if (ten.length !== 10) return (value || "").toString();
      return ten.slice(0, 3) + "-" + ten.slice(3, 6) + "-" + ten.slice(6);
    },
    deadlinePairMessage(time, phone) {
      const parsed = timeValue(time);
      if (time && !parsed) return "Enter a time like 3:30 PM.";
      const digits = (phone || "").toString().replace(/\D/g, "");
      const hasPhone = digits.length > 0;
      if (!!parsed === hasPhone) return "";
      return "Enter both a deadline time and a phone number, or leave both empty.";
    },
    openDeadlineDialog() {
      const selected = this.$refs.grid.getSelectedRowsData() || [];
      if (!selected.length) return;
      this.deadlineSelection = selected;
      const times = this.unique(selected.map((row) => timeValue(row.deadlineTime)));
      const phones = this.unique(
        selected.map((row) => (row.afterDeadlinePhone || "").toString().trim())
      );
      this.deadlineTime = times.length === 1 ? toDeadlineDate(times[0]) : null;
      this.deadlinePhone = phones.length === 1 ? this.formatPhone(phones[0]) : "";
      this.deadlineDialog = true;
      this.loadDeadlineChoices();
    },
    async saveDeadline() {
      const phoneOk =
        !this.$refs.bulkDeadlinePhone ||
        this.$refs.bulkDeadlinePhone.validate(true);
      if (!phoneOk) return;

      const message = this.deadlinePairMessage(
        this.deadlineTime,
        this.deadlinePhone
      );
      if (message) {
        this.$notify({
          group: "notification",
          text: message,
          type: "error",
        });
        return;
      }
      const digits = (this.deadlinePhone || "").toString().replace(/\D/g, "");
      if (
        digits &&
        digits.length !== 10 &&
        !(digits.length === 11 && digits.charAt(0) === "1") &&
        digits.length !== 7
      ) {
        this.$notify({
          group: "notification",
          text: "Use a 10 digit phone number.",
          type: "error",
        });
        return;
      }

      this.savingDeadline = true;
      try {
        await this.$http.post("api/marathonPickupContacts/setDeadline", {
          ids: this.deadlineSelection.map((row) => row.id),
          deadlineTime: timeValue(this.deadlineTime),
          afterDeadlinePhone: this.deadlinePhone,
        });
        this.deadlineDialog = false;
        this.$refs.grid.reloadGrid();
        this.loadDeadlineChoices();
        this.$notify({
          group: "notification",
          text: this.deadlineTime
            ? "Deadline saved."
            : "Deadline removed.",
          type: "success",
        });
      } finally {
        this.savingDeadline = false;
      }
    },
    async loadClassOptions() {
      const options = await this.$http.get(
        "api/marathonPickupContacts/classOptions"
      );
      this.classOptions = options || [];
    },
    unique(values) {
      const seen = {};
      const list = [];
      (values || []).forEach((value) => {
        const text = (value || "").toString().trim();
        if (!text || seen[text]) return;
        seen[text] = true;
        list.push(text);
      });
      return list.sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
      );
    },
    branchRule(value) {
      if (!value) return "Required";
      return this.branchItems.indexOf(value) === -1
        ? "Pick a branch from the list"
        : true;
    },
    classRule(value) {
      if (!value) return "Required";
      return this.classItems.indexOf(value) === -1
        ? "Pick a class from the list"
        : true;
    },
    sectionRule(value) {
      if (!value) return "Required";
      return this.sectionItems.indexOf(value) === -1
        ? "Pick a section from the list"
        : true;
    },
    onBranchPicked() {
      if (this.assigningRow) return;
      if (this.row.class && this.classItems.indexOf(this.row.class) === -1) {
        this.row.class = "";
        this.row.section = "";
      } else if (
        this.row.section &&
        this.sectionItems.indexOf(this.row.section) === -1
      ) {
        this.row.section = "";
      }
    },
    onClassPicked() {
      if (this.assigningRow) return;
      if (this.row.section && this.sectionItems.indexOf(this.row.section) === -1) {
        this.row.section = "";
      }
    },
    keepCurrentOption() {
      const branch = (this.row.branch || "").trim();
      const className = (this.row.class || "").trim();
      const section = (this.row.section || "").trim();
      if (!branch || !className || !section) return;
      const exists = this.classOptions.some(
        (option) =>
          option.branch === branch &&
          option.class === className &&
          option.section === section
      );
      if (!exists) {
        this.classOptions.push({
          branch: branch,
          class: className,
          section: section,
        });
      }
    },
    addContact() {
      this.row = emptyRow();
      this.editDialog = true;
    },
    onRowClicked(row) {
      const copy = JSON.parse(JSON.stringify(row));
      copy.branch = (copy.branch || "").trim();
      copy.class = (copy.class || "").trim();
      copy.section = (copy.section || "").trim();
      copy.deadlineTime = toDeadlineDate(copy.deadlineTime);
      this.assigningRow = true;
      this.row = copy;
      this.keepCurrentOption();
      this.editDialog = true;
      this.$nextTick(() => {
        this.assigningRow = false;
      });
    },
    digits(value) {
      return (value || "").toString().replace(/\D/g, "");
    },
    async save() {
      const fields = [
        "branch",
        "className",
        "section",
        "did",
        "alertPhone",
        "cellPhone",
        "subCell",
        "afterDeadlinePhone",
      ];
      const valid = fields.every((name) => this.$refs[name].validate(true));
      if (!valid) return;
      const deadlineMessage = this.deadlinePairMessage(
        this.row.deadlineTime,
        this.row.afterDeadlinePhone
      );
      if (deadlineMessage) {
        this.$notify({
          group: "notification",
          text: deadlineMessage,
          type: "error",
        });
        return;
      }

      this.processing = true;
      try {
        const data = JSON.parse(JSON.stringify(this.row));
        data.employeeID = data.employeeID ? Number(data.employeeID) : null;
        data.deadlineTime = timeValue(data.deadlineTime);
        await this.$http.post("api/marathonPickupContacts/save", data);
        this.editDialog = false;
        this.$refs.grid.reloadGrid();
        this.loadDeadlineChoices();
        this.$notify({
          group: "notification",
          text: "Saved.",
          type: "success",
        });
      } finally {
        this.processing = false;
      }
    },
    async remove() {
      this.processing = true;
      try {
        await this.$http.post(
          "api/marathonPickupContacts/delete?id=" + this.row.id
        );
        this.confirmDelete = false;
        this.editDialog = false;
        this.$refs.grid.reloadGrid();
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>
